import React, { FC, useContext } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  StyleProp,
  TextStyle,
  Clipboard,
} from "react-native";
import EmptyView from "../../components/common/EmptyView";
import Spinner from "../../components/common/Spinner";
import StyledIcon from "../../components/StyledIcon";
import Colors from "../../constants/Colors";
import FontStyles from "../../constants/FontStyle";
import { SafeJSONObject } from "../../constants/Utils";
import { AuthContext } from "../../contexts/auth/Store";
import useFetch from "../../hooks/useFetch";
import { IBuyer } from "../../interfaces/buyer";
import { ILedgerTag, LedgerHead } from "../../interfaces/ledger";
import { ledgerService } from "../../services/LedgerService";

const IconText: FC<{
  icon: string;
  text: string;
  color: string;
  size?: number;
  textStyle?: StyleProp<TextStyle>;
}> = ({ icon, text, color, size, textStyle }) => {
  return (
    <View style={styles.iconTextContainer}>
      <StyledIcon
        iconName={icon}
        iconColor={color}
        size={size || 14}
        iconOnly
        iconLibrary="MaterialCommunityIcons"
      />
      <Text
        style={[
          styles.iconTextContent,
          textStyle,
          size ? { fontSize: size } : undefined,
        ]}
      >
        {text}
      </Text>
    </View>
  );
};

export const VirtualAccount = () => {
  const auth = useContext(AuthContext);
  const buyer: IBuyer = auth.state.buyerData;

  const doCopy = (content: string) => {
    Clipboard.setString(content);
  };

  const [ledgerTag, loadingLedgerTag, errorLedgerTag] = useFetch<ILedgerTag[]>(
    ledgerService,
    "fetchTags",
    [
      {
        parent_id: buyer?._id,
        parent_type: LedgerHead.Buyer,
      },
    ],
    !!buyer?._id
  );

  const virtualAccount: any = React.useMemo(() => {
    const account_detail = ledgerTag?.find(
      (_tag) => _tag.tag === "virtual_account"
    );
    if (account_detail) {
      return SafeJSONObject(account_detail.value);
    }
  }, [ledgerTag]);

  return (
    <>
      <View style={styles.container}>
        {loadingLedgerTag ? (
          <Spinner color={Colors.tintColor} />
        ) : errorLedgerTag ? (
          <EmptyView summary="Unable to fetch account details" />
        ) : virtualAccount ? (
          <>
            <Text style={styles.label}>Bank</Text>
            <Text style={styles.microTitle}>{virtualAccount?.bank?.name}</Text>

            <Text style={styles.label}>Account No.</Text>
            <View style={styles.accNumContainer}>
              <Text style={[styles.macroTitle, { flexGrow: 1 }]}>
                {virtualAccount?.account_number}
              </Text>
              <TouchableOpacity
                onPress={() => doCopy(virtualAccount?.account_number)}
              >
                <IconText
                  icon="content-copy"
                  text="Copy"
                  color={Colors.tintColor}
                  textStyle={{
                    ...FontStyles.size13,
                    ...FontStyles.lh15,
                    ...FontStyles.medium,
                  }}
                  size={13}
                />
              </TouchableOpacity>
            </View>

            <Text style={styles.label}>Account Name</Text>
            <Text style={styles.microTitle}>
              {virtualAccount?.account_name}
            </Text>
          </>
        ) : (
          <EmptyView summary="You do not have an account." />
        )}
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  container: {},
  accNumContainer: {
    flexDirection: "row",
  },
  iconTextContainer: {
    flexDirection: "row",
    alignItems: "center",
    alignContent: "center",
    justifyContent: "center",
  },
  iconTextContent: {
    color: Colors.tintColor,
    paddingLeft: 6,
  },
  label: {
    ...FontStyles.size13,
    ...FontStyles.lh16,
    ...FontStyles.gray,
    ...FontStyles.medium,
  },
  macroTitle: {
    ...FontStyles.size18,
    ...FontStyles.lh22,
    ...FontStyles.bold,
    paddingBottom: 14,
  },
  microTitle: {
    ...FontStyles.size14,
    ...FontStyles.lh16,
    ...FontStyles.normal,
    paddingBottom: 14,
  },
});
